import { type ChartDataset } from 'chart.js';
import { DATASET_SOURCE_COLORS } from 'src/shared/types/charts';
import { type ChartValuesAmount, type ChartValuesCount } from 'src/shared/types/chartsData';
import { type CountryCodes, type CurrencyCodes } from 'src/shared/utils/currency';
import { barChartOptions, lineChartOptions } from './options';
import { CHART_TYPE, type ChartContext } from './types';

export enum AMOUNT_TOOLTIP_LABELS {
  saveflowAmount = 'saveflow',
  extraPaymentsAmount = 'extra payment',
  payflowPlusProcessingAmount = 'payflow'
}

export enum COUNT_TOOLTIP_LABELS {
  saveflowCount = 'saveflow',
  extraPaymentsCount = 'extra payment',
  payflowPlusProcessingCount = 'payflow',
  summary = 'summary'
}

const lineColorSetup = (theme: any) => {
  return {
    borderColor: theme.palette.charts.line.color,
    pointBorderColor: '#FFFFFF',
    pointRadius: 6,
    pointBorderWidth: 3
  };
};

export const renderBackgroundColor =
  (chartType: CHART_TYPE, theme: any, key?: string) =>
  (context: ChartContext): string => {
    const { chart } = context;
    const { _, chartArea } = chart;
    if (!chartArea) {
      return '';
    }
    if (chartType === CHART_TYPE.BAR && key) {
      const barColor = DATASET_SOURCE_COLORS[key as keyof typeof DATASET_SOURCE_COLORS];
      return barColor;
    }
    return theme.palette.charts.line.color;
  };

export const getChartOptions = (
  type: CHART_TYPE,
  theme: any,
  currency: CurrencyCodes,
  locale: CountryCodes,
  stacked: boolean
) => {
  switch (type) {
    case CHART_TYPE.BAR:
      return barChartOptions(stacked, theme, currency, locale);
    case CHART_TYPE.LINE:
      return lineChartOptions(theme, stacked);
    default:
      return {};
  }
};

const getSummaryDataset = (dataSets: ChartDataset[]) => {
  const summary = [...dataSets].reduce(
    (acc, dataValues) => {
      return dataValues?.data?.map((val, index) => Number(acc[index] || 0) + Number(val ?? 0));
    },
    [...Array(12).fill(0)]
  );
  return summary;
};

const genLineChartDatasets = (data: ChartValuesAmount | ChartValuesCount, theme: any) => {
  const dataset = Object.entries(data).reduce<any[]>((acc, dataValues, index) => {
    const [key, values] = dataValues;

    acc.push({
      data: values,
      label: key,
      borderWidth: 0,
      fill: false,
      pointStyle: false,
      ...lineColorSetup(theme),
      backgroundColor: DATASET_SOURCE_COLORS.payflowPlusProcessingCount
    });

    return acc;
  }, []);

  const summaryDataset = getSummaryDataset(dataset);

  return [
    ...dataset,
    {
      data: summaryDataset,
      label: COUNT_TOOLTIP_LABELS.summary,
      backgroundColor: theme.palette.charts.line.color,
      ...lineColorSetup(theme)
    }
  ];
};

const genBarChartDatasets = (data: ChartValuesAmount | ChartValuesCount, chartType: CHART_TYPE, theme: any) => {
  const dataset = Object.entries(data).reduce<any[]>((acc, dataValues) => {
    const [key, values] = dataValues;

    acc.push({
      data: values,
      label: key,
      backgroundColor: renderBackgroundColor(chartType, theme, key)
    });

    return acc;
  }, []);

  return dataset;
};

export const generateChartDatasets = (
  data: ChartValuesAmount | ChartValuesCount,
  chartType: CHART_TYPE,
  theme: any
) => {
  if (chartType === CHART_TYPE.LINE) {
    return genLineChartDatasets(data, theme);
  }

  return genBarChartDatasets(data, chartType, theme);
};
